import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { graphql } from 'gatsby';
import Flickity from 'flickity-fade';
import { sortBy } from 'lodash-es';
import axios from 'axios';
import { List, ListItem } from 'chaoskit/src/components';

import BaseLayout from '../layouts/BaseLayout';
import { Data, Icon, Link } from '../components';
import Hero from '../partials/Hero';
import CustomerLogos from '../partials/global/CustomerLogos';
import CaseStudyQuote from '../partials/customers/CaseStudyQuote';
import StaticFeaturedArticles from '../partials/global/StaticFeaturedArticles';

const CaseStudyTeasers = ({ caseStudies, featuredCaseStudy }) => {
  // Find featured case study and re-order in group
  // @NOTE This will be revised in the future to ONLY show a featured case study once we introduce filtering
  const sortedCaseStudies = sortBy(caseStudies, [
    // eslint-disable-next-line func-names
    function(caseStudy) {
      return caseStudy.node.frontmatter.title === featuredCaseStudy ? 0 : 1;
    },
  ]);

  return (
    <Fragment>
      {sortedCaseStudies.map(({ node }) => {
        // Use teaser logo if available
        const logo =
          node.frontmatter.hero.teaserLogo || node.frontmatter.hero.logo;

        return (
          <Link
            key={node.fileAbsolutePath}
            className="customers__featuredTeaser"
            style={{
              backgroundImage: `url(${node.frontmatter.hero.background})`,
            }}
            to={node.fields.fullUrl}
          >
            <div className="customers__featuredTeaser-content">
              <div className="row row--collapse">
                <div className="column-7@medium u-contrast">
                  <div className="customers__featuredTeaser-text">
                    <img
                      src={logo}
                      className="caseStudy__hero-logo"
                      alt={node.frontmatter.title}
                    />
                    <h2 className="hero-title u-textFluid--h2-h3 u-mt--remove">
                      <Data>{node.frontmatter.hero.heading}</Data>
                    </h2>
                    <div className="hero-titleSub">
                      <Data markdown>{node.frontmatter.hero.blurb}</Data>
                    </div>
                    <div className="u-mt--large u-textHeading u-textPrimary customers__featuredTeaser-more">
                      Learn More
                      <Icon icon="caret-right" className="u-ml--small" />
                    </div>
                  </div>
                </div>
                <div className="column-5@medium customers__featuredTeaser-metaColumn">
                  <div className="customers__featuredTeaser-callout">
                    <List type={['space']}>
                      {node.frontmatter.results.map(result => (
                        <ListItem
                          key={result.heading}
                          className="u-textHeading u-textCenter u-textLeft@medium"
                        >
                          <div className="u-textFluid--h3-h4 u-textPrimary">
                            <Data>{result.heading}</Data>
                          </div>
                          <div className="u-textNormal u-textDefault u-textFluid--large">
                            <Data markdown>{result.blurb}</Data>
                          </div>
                        </ListItem>
                      ))}
                    </List>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        );
      })}
    </Fragment>
  );
};

CaseStudyTeasers.propTypes = {
  caseStudies: PropTypes.array.isRequired,
  featuredCaseStudy: PropTypes.string.isRequired,
};

const FeaturedQuote = ({ caseStudies, featuredCaseStudy }) => {
  // Find featured case study
  const featuredCaseStudyMatch = caseStudies.find(
    caseStudy => caseStudy.node.frontmatter.title === featuredCaseStudy
  );

  if (!featuredCaseStudyMatch) return null;

  const {
    node: { frontmatter },
  } = featuredCaseStudyMatch;

  return (
    <section className="section section--large customers__gradient u-contrast">
      <div className="container">
        <CaseStudyQuote
          className="is-overview"
          text={frontmatter.quote.text}
          name={frontmatter.quote.name}
          title={frontmatter.quote.title}
          logo={frontmatter.hero.logo}
        />
      </div>
    </section>
  );
};

FeaturedQuote.propTypes = {
  caseStudies: PropTypes.array.isRequired,
  featuredCaseStudy: PropTypes.string.isRequired,
};

const CustomerTweet = ({ tweet }) => (
  <article className="customers__tweet">
    <div className="customers__tweet-header">
      <img src={tweet.user.profile_image_url_https} alt={tweet.user.name} />
      <div className="customers__tweet-info">
        <div className="u-textBold u-textMedium u-textHeading">
          {tweet.user.name}
        </div>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`https://www.twitter.com/${tweet.user.screen_name}`}
          className="u-linkDefault u-textSmall u-linkUnderline"
        >
          @{tweet.user.screen_name}
        </a>
      </div>
      <a
        target="_blank"
        rel="noopener noreferrer"
        className="u-linkMuted customers__tweet-birdy"
        href={`https://www.twitter.com/${tweet.user.screen_name}/statuses/${tweet.id_str}`}
      >
        <Icon size="large" icon="twitter" />
      </a>
    </div>
    <div className="customers__tweet-body u-textSmall">
      <p>{tweet.full_text}</p>
    </div>
  </article>
);

CustomerTweet.propTypes = {
  tweet: PropTypes.object.isRequired,
};

const Customers = props => {
  const {
    data: {
      pageData: { childDataYaml: pageData },
      allCaseStudies: { edges: allCaseStudies },
    },
  } = props;
  const [tweets, setTweets] = useState([]);

  const sliderRef = useCallback(
    node => {
      if (node !== null) {
        // eslint-disable-next-line no-unused-vars
        const flickity = new Flickity(node, {
          draggable: false,
          prevNextButtons: false,
          fade: true,
          percentPosition: true,
          groupCells: '100%',
        });
      }
    },
    [tweets]
  );

  useEffect(() => {
    // Grab IDs from statuses
    const statusList = pageData.community.tweets
      .map(({ status }) => {
        const regex = /http(s)?:\/\/twitter\.com\/\w+\/status(es)?\/(\d+)/;

        const match = status.match(regex);

        if (match) {
          return match[3];
        }

        return false;
      })
      .join(',');

    axios
      .get(`/.netlify/functions/tweets?statuses=${statusList}`)
      .then(({ data: { statuses } }) => {
        if (!statuses) return;

        setTweets(statuses);
      });
  }, []);

  return (
    <BaseLayout>
      <Helmet
        title={pageData.title}
        meta={[{ name: 'description', content: pageData.description }]}
      />
      <Hero particles={false} compact className="customers__gradient">
        <div className="hero-content">
          <div className="container">
            <div className="hero-titleWrapper">
              <h1 className="hero-title">
                <Data>{pageData.hero.heading}</Data>
              </h1>
              <div className="hero-titleSub">
                <Data markdown>{pageData.hero.blurb}</Data>
              </div>
            </div>
          </div>
        </div>
      </Hero>
      <section className="section section--large u-bgPanel">
        <div className="container">
          <CaseStudyTeasers
            caseStudies={allCaseStudies}
            featuredCaseStudy={pageData.featuredCaseStudy}
          />
        </div>
      </section>
      <FeaturedQuote
        caseStudies={allCaseStudies}
        featuredCaseStudy={pageData.featuredCaseStudy}
      />
      <CustomerLogos />
      {tweets.length > 0 && (
        <section className="section section--large u-gradient--green-teal">
          <div className="container container--extended">
            <div className="section-titleWrapper u-contrast">
              <h3 className="section-title u-textFluid--h2-h3">
                <Data>{pageData.community.heading}</Data>
              </h3>
            </div>

            <div className="customers__community-slider" ref={sliderRef}>
              {tweets.map(tweet => (
                <CustomerTweet key={tweet.id_str} tweet={tweet} />
              ))}
            </div>
          </div>
        </section>
      )}
      <StaticFeaturedArticles />
    </BaseLayout>
  );
};

Customers.propTypes = {
  data: PropTypes.object.isRequired,
};

export const pageQuery = graphql`
  query CustomersPageData {
    pageData: file(name: { eq: "customers" }) {
      childDataYaml {
        title
        description
        hero {
          blurb
          heading
        }
        featuredCaseStudy
        community {
          heading
          tweets {
            status
          }
        }
      }
    }

    allCaseStudies: allMarkdownRemark(
      filter: {
        # Make sure we're only querying case studies
        fileAbsolutePath: { regex: "/src/case-studies/" }
        frontmatter: { title: { ne: "BLUEPRINT" } }
      }
      sort: { fields: [frontmatter___title], order: ASC }
    ) {
      edges {
        node {
          ...CaseStudyTeaserFragment
        }
      }
    }
  }
`;

export default Customers;
